import { useEffect, useRef, useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from "@mui/material";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import { pushPermissionState, pushPrePromptState } from "@/lib/store";
import { isPrePromptBlockedPath, isPrePromptSnoozed, isPushSupported, snoozePrePrompt } from "@/lib/push";
import { requestPushPermission, usePushNativeReady } from "src/hooks/usePushNotifications";

// Seconds after the app is ready before the launch pre-prompt appears, so it does not
// land on top of the first paint.
const LAUNCH_DELAY_MS = 1500;

interface PushPrePromptProps {
    pathname: string;
    // The app has finished loading the user (signed in or not).
    ready: boolean;
}

/**
 * Our own "Turn on notifications" screen, shown before the OS permission prompt
 * (ortex/ted/ted#2485). Only in the app, only while the OS permission is still
 * undecided. "Not now" snoozes the launch prompt for 7 days. Signed-out users see it
 * too (they can receive news and offers).
 */
const PushPrePrompt = ({ pathname, ready }: PushPrePromptProps) => {
    const [permission, setPermission] = useRecoilState(pushPermissionState);
    const [state, setState] = useRecoilState(pushPrePromptState);
    const [busy, setBusy] = useState(false);
    const launchHandledRef = useRef(false);
    const blocked = isPrePromptBlockedPath(pathname);
    const nativeReady = usePushNativeReady();

    useEffect(() => {
        if (launchHandledRef.current || !ready || !nativeReady || blocked || permission !== "prompt" || !isPushSupported()) return;
        if (isPrePromptSnoozed()) {
            launchHandledRef.current = true;
            return;
        }
        const t = setTimeout(() => {
            launchHandledRef.current = true;
            setState((s) => (s.open ? s : { open: true, reason: "launch" }));
        }, LAUNCH_DELAY_MS);
        return () => clearTimeout(t);
    }, [ready, nativeReady, blocked, permission, setState]);

    const close = () => setState((s) => ({ ...s, open: false }));

    const turnOn = async () => {
        setBusy(true);
        try {
            setPermission(await requestPushPermission());
        } catch (e) {
            console.warn("Push permission request failed", e);
        } finally {
            setBusy(false);
            close();
        }
    };

    const notNow = () => {
        snoozePrePrompt();
        close();
    };

    const open = state.open && nativeReady && permission === "prompt" && (state.reason !== "launch" || !blocked);
    if (!open) return null;

    const body =
        state.reason === "alert"
            ? "To get this alert on your phone, turn on notifications. You will also get market-moving news and occasional offers from ORTEX, which you can switch off in Settings."
            : "Get the alerts you set, market-moving news and occasional offers from ORTEX on your phone.";

    return (
        <Dialog open onClose={notNow} fullWidth maxWidth="xs" aria-labelledby="push-preprompt-title" PaperProps={{ sx: { m: 2, width: "calc(100% - 32px)", borderRadius: 3 } }}>
            <DialogContent sx={{ pt: 5, pb: 2, px: 3, textAlign: "center" }}>
                <Box
                    sx={{
                        width: 72,
                        height: 72,
                        mx: "auto",
                        mb: 3,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                    }}
                >
                    <NotificationsActiveOutlinedIcon sx={{ fontSize: 36 }} />
                </Box>
                <Typography id="push-preprompt-title" variant="h2" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Turn on notifications
                </Typography>
                <Typography variant="body1">{body}</Typography>
            </DialogContent>
            <DialogActions sx={{ flexDirection: "column", gap: 1, px: 3, pb: 3, "& > :not(style) ~ :not(style)": { ml: 0 } }}>
                <Button variant="contained" fullWidth size="large" onClick={turnOn} disabled={busy}>
                    Turn on
                </Button>
                <Button variant="text" fullWidth onClick={notNow} disabled={busy}>
                    Not now
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PushPrePrompt;
